
import React from 'react';
import { Campaign, Brand } from '../types';
import { CampaignStatus } from '../enums';
import { Calendar, ArrowRight, TrendingUp, MousePointerClick } from 'lucide-react';
import { asArray } from '../utils/asArray';

interface ActiveCampaignGridProps {
  campaigns: Campaign[];
  brands: Brand[];
  onSelect: (campaign: Campaign) => void;
  onDashboard?: () => void;
  currencyCode?: string;
}

export const ActiveCampaignGrid: React.FC<ActiveCampaignGridProps> = ({ campaigns, brands, onSelect, onDashboard, currencyCode }) => {
  const currency = currencyCode || 'QAR';
  const safeCampaigns = asArray(campaigns);
  const safeBrands = asArray(brands);
  const activeCampaigns = safeCampaigns.filter(c => c.status === CampaignStatus.ACTIVE);
  const brandMap = new Map(safeBrands.map((b) => [b.id, b.name]));

  const getBrandNames = (campaign: Campaign) => {
    const rawIds = Array.isArray(campaign.brandIds)
      ? campaign.brandIds
      : campaign.brandIds
      ? [campaign.brandIds as any]
      : [];
    const fallback = Array.isArray((campaign as any).brands)
      ? (campaign as any).brands.map((b: any) => b.id).filter(Boolean)
      : [];
    const ids = rawIds.length ? rawIds : fallback;
    const names = asArray(ids).map((id) => brandMap.get(id) || id).filter(Boolean);
    return names;
  };

  const formatBrandLine = (campaign: Campaign) => {
    const names = getBrandNames(campaign);
    if (!names.length) return null;
    const label = names.length > 1 ? 'Brands' : 'Brand';
    const display = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3} more` : names.join(', ');
    return { label, display };
  };
  const formatFunding = (campaign: Campaign) => {
    const isPercent = campaign.discountType === 'Percentage';
    const sterling = campaign.sterlingPercent ?? (campaign.fundingType === 'Fully Funded' ? 100 : undefined);
    const partner = campaign.coFundedPercent ?? (campaign.fundingType === 'Co-Funded' ? 0 : undefined);
    const dv = Number(campaign.discountValue) || 0;

    if (campaign.fundingType === 'Co-Funded') {
      if (isPercent) return `Sterling ${sterling ?? '-'}% / Partner ${partner ?? '-'}%`;
      const sAmt = sterling != null ? (dv * (sterling / 100)).toFixed(2) : '-';
      const pAmt = partner != null ? (dv * (partner / 100)).toFixed(2) : '-';
      return `Sterling ${currency} ${sAmt} / Partner ${currency} ${pAmt}`;
    }
    if (campaign.fundingType === 'Fully Funded') {
      if (isPercent) return `Sterling ${sterling ?? 100}%`;
      return `Sterling ${currency} ${dv.toFixed(2)}`;
    }
    return 'None';
  };

  const tooltipContent = (campaign: Campaign) => {
    const discountText = campaign.discountType === 'Percentage'
      ? `Discount: ${campaign.discountValue}% off`
      : `Discount: ${campaign.discountValue.toFixed(2)} ${currency} off`;
    const fundingText = campaign.fundingType === 'Co-Funded'
      ? `Funding: Sterling ${campaign.sterlingPercent ?? '-'}% / Co-Funder ${campaign.coFundedPercent ?? '-'}% of discount`
      : campaign.fundingType === 'Fully Funded'
        ? 'Funding: Sterling 100% of discount'
        : 'Funding: None';
    const dateText = campaign.startDate && campaign.endDate ? `Valid: ${campaign.startDate} → ${campaign.endDate}` : '';
    return [discountText, fundingText, dateText].filter(Boolean).join(' • ');
  };

  if (activeCampaigns.length === 0) {
    return (
      <div className="animate-fade-in w-full max-w-4xl mx-auto px-4 pb-20 text-center">
        <div className="text-3xl font-extrabold text-zinc-900 mb-3">No active campaigns</div>
        <p className="text-zinc-500">There are currently no active campaigns available for your selection.</p>
        {onDashboard && (
          <div className="mt-6">
            <button 
              onClick={onDashboard}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 rounded-xl font-bold shadow-md shadow-zinc-900/10 hover:bg-zinc-800 transition-colors"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fade-in w-full max-w-7xl mx-auto px-4 pb-20">
      {onDashboard && (
        <div className="flex justify-end mb-4">
          <button 
            onClick={onDashboard}
            className="flex items-center gap-2 text-zinc-500 hover:text-teal-600 font-bold transition-all bg-white px-5 py-2.5 rounded-xl border border-zinc-200 shadow-sm hover:shadow-md active:scale-95"
          >
             Dashboard <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="text-center mb-12 mt-8">
        <h1 className="text-4xl md:text-6xl font-extrabold text-zinc-900 tracking-tight mb-6">
          Active Campaigns
        </h1>
        <p className="text-xl text-zinc-500 max-w-2xl mx-auto font-medium">
          Select an active campaign below to begin your discount submission process.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {activeCampaigns.map((campaign) => {
          const brandLine = formatBrandLine(campaign);
          return (
          <div 
            key={campaign.id}
            onClick={() => onSelect(campaign)}
            className="group bg-white rounded-[2.5rem] p-10 border border-zinc-200 shadow-sm hover:shadow-2xl hover:shadow-teal-900/10 hover:border-teal-200 hover:-translate-y-2 transition-all duration-500 cursor-pointer flex flex-col h-full relative overflow-visible"
          >
            {/* Decorative Gradient Background on Hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-50/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            
            <div className="relative z-10 flex flex-col h-full">
                <div className="flex justify-between items-start mb-8">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-50 text-teal-700 text-xs font-bold uppercase tracking-wide border border-teal-100 group-hover:bg-teal-100 transition-colors">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Active Campaign
                    </span>
                    <div className="bg-zinc-50 p-3 rounded-full text-zinc-400 group-hover:bg-white group-hover:text-teal-600 group-hover:shadow-md transition-all duration-500 transform group-hover:rotate-[-45deg]">
                        <ArrowRight className="w-6 h-6" />
                    </div>
                </div>

                <h3 className="text-3xl font-extrabold text-zinc-900 mb-4 leading-tight group-hover:text-teal-900 transition-colors">
                    {campaign.name}
                </h3>
                
                <p className="text-base text-zinc-500 line-clamp-3 mb-10 flex-1 leading-relaxed font-medium group-hover:text-zinc-600 transition-colors">
                    {campaign.description}
                </p>
                {brandLine && (
                  <div className="text-[11px] text-zinc-600 font-semibold bg-zinc-100 px-3 py-2 rounded-lg inline-flex gap-1 mb-6">
                    <span className="uppercase text-[10px] text-zinc-400 tracking-wide">{brandLine.label}:</span>
                    <span className="text-zinc-800">{brandLine.display}</span>
                  </div>
                )}

                <div className="mt-auto">
                    <div className="flex items-center gap-3 text-xs font-bold text-zinc-400 uppercase tracking-wider bg-zinc-50 p-3 rounded-xl w-fit border border-zinc-100 group-hover:border-zinc-200/50 group-hover:bg-white/80 transition-all mb-6">
                         <Calendar className="w-4 h-4 text-zinc-300 group-hover:text-teal-400 transition-colors" />
                         {campaign.startDate} - {campaign.endDate}
                    </div>
                    
                    <div className="pt-6 border-t border-zinc-100 flex items-center justify-between group/footer">
                         <div className="flex flex-col gap-1">
                             <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Discount</span>
                            <div className="relative group/tooltip">
                               <div className="flex items-baseline gap-2">
                                  <span className="text-2xl font-extrabold text-teal-700 tracking-tight">{campaign.discountValue}</span>
                                  <span className="text-sm font-bold text-teal-600/80 uppercase">{campaign.discountType === 'Percentage' ? '% OFF' : `${currency} OFF`}</span>
                               </div>
                               <div className="absolute left-0 top-full mt-1 hidden group-hover/tooltip:block bg-white border border-zinc-200 shadow-lg rounded-xl p-3 text-xs text-zinc-700 w-64 z-30">
                                  <p className="font-bold text-zinc-900 mb-1">{campaign.name}</p>
                                  <p>{tooltipContent(campaign)}</p>
                               </div>
                             </div>
                             <div className="text-[11px] text-zinc-500 font-semibold flex items-center gap-2">
                                <span className="uppercase text-[10px] text-zinc-400">Funding</span>
                                <span className="font-bold text-zinc-600">{campaign.fundingType || 'None'} · {formatFunding(campaign)}</span>
                             </div>
                         </div>
                         <span className="text-sm font-bold text-zinc-300 group-hover:text-teal-600 transition-colors flex items-center gap-2 opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 duration-300">
                            Start <MousePointerClick className="w-4 h-4" />
                         </span>
                    </div>
                </div>
            </div>
          </div>
        )})}
      </div>
      
      {activeCampaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center py-32 bg-white/60 backdrop-blur-xl rounded-[3rem] border-2 border-dashed border-zinc-200 text-center">
              <div className="mb-6 bg-zinc-50 w-24 h-24 rounded-full flex items-center justify-center shadow-inner">
                  <Calendar className="w-10 h-10 text-zinc-300" />
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 mb-2">No Active Campaigns</h3>
              <p className="text-zinc-500 max-w-md mx-auto">There are currently no active discount campaigns available for submission. Please check back later.</p>
          </div>
      )}
    </div>
  );
};
